import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Edit,
  DollarSign,
  Building,
  Target,
  Settings,
  Loader2
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { 
  PLATFORM_PRICING_TIERS, 
  revenueBandToPlatformTier,
  platformTierToROITier 
} from '@/utils/pricingTierMapper';

interface PlatformPricing {
  id: string;
  revenue_band: string;
  list_price: number;
  created_at: string;
  updated_at: string;
}

interface PillarPricing {
  id: string;
  revenue_band: string;
  pillar_name: string;
  list_price: number;
  created_at: string;
  updated_at: string;
}

interface EntityPricingTier {
  id: string;
  min_entities: number;
  max_entities: number | null;
  effective_units: number;
  price_modifier: number;
  created_at: string;
  updated_at: string;
}

export default function AdminPricingTiersScreen() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [editingPlatform, setEditingPlatform] = useState<PlatformPricing | null>(null);
  const [editingPillar, setEditingPillar] = useState<PillarPricing | null>(null);
  const [editingEntity, setEditingEntity] = useState<EntityPricingTier | null>(null);
  const [activeTab, setActiveTab] = useState("platform");

  // Fetch platform pricing data
  const { data: platformPricing = [], isLoading: platformLoading } = useQuery<PlatformPricing[]>({
    queryKey: ['/api/admin/platform-pricing'],
  });

  // Fetch pillar pricing data
  const { data: pillarPricing = [], isLoading: pillarLoading } = useQuery<PillarPricing[]>({
    queryKey: ['/api/admin/pillar-pricing'],
  });

  // Fetch entity pricing data
  const { data: entityPricing = [], isLoading: entityLoading } = useQuery<EntityPricingTier[]>({
    queryKey: ['/api/admin/entity-pricing'],
  });

  // Platform pricing mutations
  const updatePlatformMutation = useMutation({
    mutationFn: async (updated: PlatformPricing) => {
      return apiRequest(`/api/admin/platform-pricing/${updated.id}`, {
        method: 'PUT',
        body: updated,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/platform-pricing'] });
      setEditingPlatform(null);
      toast({
        title: "Success",
        description: "Platform pricing updated successfully",
      });
    },
    onError: (error) => {
      console.error('Update platform pricing error:', error);
      toast({
        title: "Error",
        description: "Failed to update platform pricing",
        variant: "destructive",
      });
    },
  });

  // Pillar pricing mutations
  const updatePillarMutation = useMutation({
    mutationFn: async (updated: PillarPricing) => {
      return apiRequest(`/api/admin/pillar-pricing/${updated.id}`, {
        method: 'PUT',
        body: updated,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/pillar-pricing'] });
      setEditingPillar(null);
      toast({
        title: "Success",
        description: "Pillar pricing updated successfully",
      });
    },
    onError: (error) => {
      console.error('Update pillar pricing error:', error);
      toast({
        title: "Error",
        description: "Failed to update pillar pricing",
        variant: "destructive",
      });
    },
  });

  // Entity pricing mutations
  const updateEntityMutation = useMutation({
    mutationFn: async (updated: EntityPricingTier) => {
      return apiRequest(`/api/admin/entity-pricing/${updated.id}`, {
        method: 'PUT',
        body: updated,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/entity-pricing'] });
      setEditingEntity(null);
      toast({
        title: "Success",
        description: "Entity pricing updated successfully",
      });
    },
    onError: (error) => {
      console.error('Update entity pricing error:', error);
      toast({
        title: "Error",
        description: "Failed to update entity pricing",
        variant: "destructive",
      });
    },
  });

  const isLoading = platformLoading || pillarLoading || entityLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Pricing Management</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-2">
          Manage pricing across all platform tiers and modules
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="platform" className="flex items-center gap-2">
            <Building className="h-4 w-4" />
            Platform Pricing
          </TabsTrigger>
          <TabsTrigger value="pillars" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Pillar Pricing
          </TabsTrigger>
          <TabsTrigger value="entities" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Entity Tiers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="platform" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Platform Base Pricing
              </CardTitle>
              <CardDescription>
                Core platform pricing by revenue band. These prices serve as the foundation for all calculations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Revenue Band</TableHead>
                    <TableHead>List Price</TableHead>
                    <TableHead>Platform Tier</TableHead>
                    <TableHead>ROI Tier</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {platformPricing.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.revenue_band}</TableCell>
                      <TableCell>
                        {editingPlatform?.id === item.id ? (
                          <Input
                            type="number"
                            value={editingPlatform.list_price}
                            onChange={(e) => setEditingPlatform({
                              ...editingPlatform,
                              list_price: Number(e.target.value)
                            })}
                            className="w-32"
                          />
                        ) : (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4" />
                            {item.list_price.toLocaleString()}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {revenueBandToPlatformTier(item.revenue_band)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          {platformTierToROITier(revenueBandToPlatformTier(item.revenue_band))}
                        </span>
                      </TableCell>
                      <TableCell>
                        {editingPlatform?.id === item.id ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => updatePlatformMutation.mutate(editingPlatform)}
                              disabled={updatePlatformMutation.isPending}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingPlatform(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingPlatform(item)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pillars" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Pillar-Specific Pricing
              </CardTitle>
              <CardDescription>
                Individual module pricing for each pillar by revenue band.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Revenue Band</TableHead>
                    <TableHead>Pillar Name</TableHead>
                    <TableHead>List Price</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pillarPricing.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.revenue_band}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple text-purple dark:bg-purple dark:text-purple">
                          {item.pillar_name}
                        </span>
                      </TableCell>
                      <TableCell>
                        {editingPillar?.id === item.id ? (
                          <Input
                            type="number"
                            value={editingPillar.list_price}
                            onChange={(e) => setEditingPillar({
                              ...editingPillar,
                              list_price: Number(e.target.value)
                            })}
                            className="w-32"
                          />
                        ) : (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4" />
                            {item.list_price.toLocaleString()}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingPillar?.id === item.id ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => updatePillarMutation.mutate(editingPillar)}
                              disabled={updatePillarMutation.isPending}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingPillar(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingPillar(item)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="entities" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Entity Pricing Tiers
              </CardTitle>
              <CardDescription>
                Volume-based pricing modifiers based on entity count.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Min Entities</TableHead>
                    <TableHead>Max Entities</TableHead>
                    <TableHead>Effective Units</TableHead>
                    <TableHead>Price Modifier</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entityPricing.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.min_entities.toLocaleString()}</TableCell>
                      <TableCell>
                        {item.max_entities ? item.max_entities.toLocaleString() : 'Unlimited'}
                      </TableCell>
                      <TableCell>
                        {editingEntity?.id === item.id ? (
                          <Input
                            type="number"
                            value={editingEntity.effective_units}
                            onChange={(e) => setEditingEntity({
                              ...editingEntity,
                              effective_units: Number(e.target.value)
                            })}
                            className="w-32"
                          />
                        ) : (
                          item.effective_units.toLocaleString()
                        )}
                      </TableCell>
                      <TableCell>
                        {editingEntity?.id === item.id ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={editingEntity.price_modifier}
                            onChange={(e) => setEditingEntity({
                              ...editingEntity,
                              price_modifier: Number(e.target.value)
                            })}
                            className="w-32"
                          />
                        ) : (
                          <span className="flex items-center gap-1">
                            {(item.price_modifier * 100).toFixed(0)}%
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingEntity?.id === item.id ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => updateEntityMutation.mutate(editingEntity)}
                              disabled={updateEntityMutation.isPending}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingEntity(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingEntity(item)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pricing Tier Mapping Reference */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Pricing Tier Mapping Reference</CardTitle>
          <CardDescription>
            How Value Engineering revenue bands map to platform tiers and ROI calculation tiers.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h4 className="font-semibold mb-2">Value Engineering</h4>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                <li>• $1B – $5B</li>
                <li>• $5B – $25B</li>
                <li>• &gt; $25B</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Platform Tiers</h4>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                <li>• Emerging</li>
                <li>• Enterprise</li>
                <li>• Large Enterprise</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">ROI Calculation Tiers</h4>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                <li>• Professional</li>
                <li>• Enterprise</li>
                <li>• Global</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}